import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import { apiFetch } from "../api_call";

export default function ApprovedTillDate() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const rowsPerPage = 8;

  const navigate = useNavigate();

  // ================= AUTH =================
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (!isLoggedIn || user.usrlst_role?.toLowerCase() !== "admin") {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // ================= FETCH =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiFetch("/report/admin/score-report");

        const formatted = (response.approved || []).map(item => ({
          id: item.id,
          act: item.compliance,
          particular: item.instance,
          title: "",
          description: item.description || "",
          startDate: item.start_date,
          actionDate: item.action_date,
          endDate: item.end_date,
          status: item.status,
          bu: item.business_unit || "-",
          department: item.department || "-",
          userName: item.user_name || "-",
          approversEmail: item.approvers_email || "-",
          requestDate: item.request_date || "",
          responseDate: item.response_date || "",
          evidenceDocument: null,
          evidenceType: null,
        }));

        setData(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch approved till date records.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ================= FILTER =================
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(item =>
      Object.values(item).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // ================= SORT =================
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    sortable.sort((a, b) => {
      const valA = (a[sortConfig.key] || "").toString().toLowerCase();
      const valB = (b[sortConfig.key] || "").toString().toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" />;
    return sortConfig.direction === "asc"
      ? <FaSortUp className="sort-icon" />
      : <FaSortDown className="sort-icon" />;
  };

  // ================= PAGINATION =================
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ================= EXPORT =================
  const exportToCSV = () => {
    const headers = ["Id","Compliance","Instance","Start Date","Action Date","End Date","Status","User","BU","Department"];

    const rows = sortedData.map(r => [
      r.id, r.act, r.particular, r.startDate, r.actionDate,
      r.endDate, r.status, r.userName, r.bu, r.department
    ]);

    const csv = [headers, ...rows]
      .map(e => e.map(v => `"${v ?? ""}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "approved_till_date.csv";
    link.click();
  };

  // ================= UI =================
  return (
    <div className="Approved">
      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Compliant(Till Date)</div>
      </div>

      <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "10px", marginLeft: "45px" }}>
          <button className="btn gray-btn">Compliant</button>
          <button className="btn blue-btn" onClick={() => navigate("/non_compliant")}>
            Non-Compliant
          </button>
        </div>

        <div className="table-actions" style={{ display: "flex", gap: "10px" }}>
          <div style={{
            background: "#d4edda",
            color: "#155724",
            padding: "6px 12px",
            borderRadius: "20px",
            fontWeight: "600",
            fontSize: "13px"
          }}>
            Approved: {filteredData.length}
          </div>

          <div className="search-box">
            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="search-icon" />
          </div>

          <button className="action-btn primary" onClick={exportToCSV}>
            Export
          </button>
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("id")}>Id {getSortIcon("id")}</th>
            <th>Compliance</th>
            <th>Instance</th>
            <th onClick={() => requestSort("startDate")}>Start Date</th>
            <th onClick={() => requestSort("actionDate")}>Action Date</th>
            <th onClick={() => requestSort("endDate")}>End Date</th>
            <th>Status</th>
            <th>User</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr><td colSpan="8" style={{ textAlign: "center" }}>Loading...</td></tr>
          ) : error ? (
            <tr><td colSpan="8" style={{ textAlign: "center", color: "red" }}>{error}</td></tr>
          ) : currentRows.length > 0 ? (
            currentRows.map((row, i) => (
              <tr key={i}>
                <td>{row.id}</td>
                <td>{row.act}</td>
                <td>{row.particular}</td>
                <td>{row.startDate}</td>
                <td>{row.actionDate}</td>
                <td>{row.endDate}</td>
                <td style={{ color: "green", fontWeight: 600 }}>{row.status}</td>
                <td>{row.userName}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="8" style={{ textAlign: "center" }}>No records found</td></tr>
          )}
        </tbody>
      </table>

      <div className="pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}